import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type MouseEvent,
} from 'react'
import { Cell } from '@highlight/ui'
import { MessageFeedbackPopover } from '@renderer/components/feedback'
import { feedbackSubject } from '@renderer/chat/api/feedback'
import { twMerge } from 'tailwind-merge'
import type { ReminderItem } from '../../hooks/use-reminders'
import { useReminderActions } from '../../hooks/use-reminders'
import { ReminderCheckbox, type ReminderMark } from './nested/reminder-checkbox'
import { ReminderContextMenuItems } from './nested/reminder-context-menu-items'
import { hasReminderMeta, ReminderMetaRow, ReminderTags } from './nested/reminder-meta-row'
import { ReminderSourceRow } from './nested/reminder-source-row'
import { ReminderImagePreview, ReminderThumbnail } from './reminders-inline-image-preview'
import { ReminderLinkAttachments } from './reminders-link-attachment'
import { buildHtmlWithUrlHighlights, extractUrls } from './utils/extract-urls'
import { imageFilesFromClipboard, useAttachImagesToReminder } from './utils/image-attachments'
import { isModifierPressed } from './utils/platform-modifier'
import { useTimeExpressionHighlight } from './hooks/use-time-expression-highlight'
import { parseTimeExpression } from './utils/parse-time-expression'
import { givesWayToTodoActions } from '../cells/todo-actions'
import { ReminderTodoActions } from './nested/reminder-todo-actions'
import { ReminderRowHoverButtons } from '../reminder-row-actions'
import { CellContextMenu } from '../cell-actions'
import { useViewActions } from '../../view-atoms'
import { CellPreviewCard, HOVER_CARD_TRIGGER } from '../cell-preview-card'
import { useViewPopover, ViewPopover } from '../view-popover'
import { SIDEPANEL_CELL_SURFACE, SIDEPANEL_FILLED_HOVER_SURFACE, TODO_TITLE, TODO_TITLE_TYPE } from '../cells/surface'
import { ReminderPreview } from './reminder-preview'
import { ReminderPopover } from './reminder-popover'
import { useTodoPopover } from '../todo-popover'
import { useIsReminderLingering } from '../../hooks/reminder-linger'
import { useDueState } from './hooks/use-due-state'

interface ReminderCellProps {
  item: ReminderItem
  /**
   * `row` is a reminder inside a list — Home's section and the full-list View.
   * It collapses to its title behind a hover preview card, thumbnails its
   * attachments, caps its own height, and fades out once ticked. `detail` is
   * the single reminder the Reminder View is *about*: no card (the card would
   * preview what's already on screen), full-size attachments, no height cap,
   * and it holds its place when resolved because nothing else is there.
   */
  variant: 'row' | 'detail'
  /**
   * The linger exists for lists that drop resolved reminders. It holds the row
   * in place, struck through and offering Undo, until it's retired.
   * A list that keeps resolved rows in its record (the meeting's For You, a
   * chat turn's artifacts) must skip it: nothing ever removes those rows, so
   * lingering one would only re-lay it out when the linger lapsed.
   */
  retainsResolved?: boolean
  /**
   * `todo` is the To-dos section's row: its mark, and a title held to one line
   * until the user edits it.
   */
  mark?: ReminderMark
  /**
   * `popover` opens the reminder's popover under the row on click, in place of
   * the hover card. The title is edited there, so the row's own is read-only.
   */
  opensIn?: 'row' | 'popover'
}

const ReminderCell = memo(function ReminderCell({
  item,
  variant,
  retainsResolved = false,
  mark,
  opensIn = 'row',
}: ReminderCellProps) {
  const { updateItem, markItemSeen, removeAttachment, setResolved } = useReminderActions()
  const { openView } = useViewActions()
  const popover = useViewPopover()
  const rowRef = useRef<HTMLDivElement>(null)
  const editRef = useRef<HTMLDivElement>(null)
  const savingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const opensPopover = opensIn === 'popover'
  const rowPopover = useTodoPopover(rowRef)
  const openReminderView = useCallback(
    (event?: MouseEvent) => {
      rowPopover.close()
      popover.openView('reminder', { reminderId: item.id }, { sourceLabel: 'To-dos', pointerEvent: event })
    },
    [rowPopover.close, popover.openView, item.id],
  )
  // Outside a popover, every click edits in place, ⌘/Ctrl included. The Reminder View has no
  // popout form yet, so this row deliberately withholds the pointer from
  // `openView` instead of offering a window like action and chat rows do.
  const focusTitle = useCallback(() => editRef.current?.focus(), [])
  const handleCellClick = opensPopover ? rowPopover.toggle : focusTitle
  // The pointer never opens the Reminder View's window (see `handleCellClick`),
  // so the right-click menu is the only way a row reaches it.
  const openInWindow = useCallback(
    () => openView('reminder', { reminderId: item.id }, { sourceLabel: 'Reminders', popout: true }),
    [item.id, openView],
  )
  const [isCellFocused, setIsCellFocused] = useState(false)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [ctxOpen, setCtxOpen] = useState(false)
  const [feedbackType, setFeedbackType] = useState<'positive' | 'negative' | null>(null)
  const feedback = feedbackSubject(item)
  const isRow = variant === 'row'
  // Lines under the title start where it does: past the mark and its 8px gap.
  const detailIndent = mark === 'todo' ? 'pl-6' : 'pl-7'
  const held = useIsReminderLingering(item.id)
  // Held too, as a tick To-dos' countdown is holding back hasn't reached the
  // cache, nor the lists that read their own query.
  const resolved = item.resolved === true || held
  // Just ticked off and still holding its place in a list that has otherwise
  // dropped it. Rows only — those are exactly the lists that drop resolved
  // reminders; the detail View's cell stays put regardless.
  const lingering = held && isRow && !retainsResolved
  // Rows with a hover card collapse to their title, with attribution, links and
  // attachments moving to the card (Figma 6726:50553). Resolved rows keep their
  // full body: they get no card (nothing left to "mark as done"), so collapsing
  // them would strand that detail. A lingering row is the exception: it holds
  // the slot it was ticked in, and growing a source row there shoves the list
  // around. So is a `retainsResolved` list, whose resolved rows stay forever —
  // one full body per tick would grow the record without end, so they hold as a
  // single struck-through line with the detail living in the Reminder View.
  // Deliberately independent of the transient states that suppress the card —
  // a row must not change height mid-interaction.
  const collapsed = isRow && (!resolved || lingering || retainsResolved)
  // A collapsed row drops the source row, so a due date or a pin would go
  // unsaid — the meta row carries them instead, or a to-do's tags. Data-driven
  // only: a row must not change height mid-interaction.
  const showsTags = isRow && mark === 'todo'
  // A ticked row has no moves, so its tags must not give way to them.
  const showsTodoActions = showsTags && !resolved
  // Only a to-do's mark and tags take the tone, so other rows run no timer for it.
  const due = useDueState(mark === 'todo' ? item.scheduledAt : undefined)
  const showMeta = collapsed && !showsTags && hasReminderMeta(item)
  const { handleHighlightInput, currentMatch } = useTimeExpressionHighlight(editRef)
  const showSaving = useCallback(() => {
    setIsSaving(true)
    if (savingTimerRef.current) clearTimeout(savingTimerRef.current)
    savingTimerRef.current = setTimeout(() => setIsSaving(false), 800)
  }, [])

  const attachImages = useAttachImagesToReminder(item.id, showSaving)

  useEffect(() => {
    return () => {
      if (savingTimerRef.current) clearTimeout(savingTimerRef.current)
    }
  }, [])

  // Single URL extraction per title — shared by the highlight pass, the link
  // pills, and the hasLinks flag (previously each ran its own regex scan).
  const urls = useMemo(() => extractUrls(item.title), [item.title])

  useEffect(() => {
    if (!editRef.current) return
    if (urls.length === 0) {
      // Fast path for the common no-URL case: plain text node, no regex
      // re-scan and no HTML parsing on mount.
      editRef.current.textContent = item.title
    } else {
      editRef.current.innerHTML = buildHtmlWithUrlHighlights(item.title)
    }
  }, [item.title, urls])

  const getTextContent = useCallback(() => {
    return editRef.current?.innerText ?? ''
  }, [])

  const renderHighlighted = useCallback((text: string) => {
    if (editRef.current) editRef.current.innerHTML = buildHtmlWithUrlHighlights(text)
  }, [])

  const commitEdit = () => {
    const trimmed = getTextContent().trim()

    if (trimmed && trimmed !== item.title) {
      updateItem(item.id, trimmed)
      showSaving()
      renderHighlighted(trimmed)
    } else if (!trimmed) {
      renderHighlighted(item.title)
    } else {
      renderHighlighted(trimmed)
    }
  }

  const commitWithTimeParse = () => {
    const rawValue = getTextContent().trim()

    if (!rawValue) {
      renderHighlighted(item.title)
      return
    }

    const match = currentMatch ?? parseTimeExpression(rawValue)

    if (match) {
      const strippedTitle = (rawValue.slice(0, match.startIndex) + rawValue.slice(match.endIndex))
        .replace(/ {2,}/g, ' ')
        .trim()
      const finalTitle = strippedTitle || rawValue
      updateItem(item.id, finalTitle, match.date)
      showSaving()
      renderHighlighted(finalTitle)
    } else if (rawValue !== item.title) {
      updateItem(item.id, rawValue)
      showSaving()
      renderHighlighted(rawValue)
    } else {
      renderHighlighted(rawValue)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      commitWithTimeParse()
      editRef.current?.blur()
    }
  }

  const handleInput = useCallback(() => {
    handleHighlightInput()
  }, [handleHighlightInput])

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      const imageFiles = imageFilesFromClipboard(event.clipboardData)
      if (imageFiles.length === 0) return

      event.preventDefault()
      event.stopPropagation()
      void attachImages(imageFiles)
    },
    [attachImages],
  )

  const Attachment = isRow ? ReminderThumbnail : ReminderImagePreview
  const hasAttachments = (item.attachments?.length ?? 0) > 0
  const hasLinks = urls.length > 0

  const handleEditableClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!isModifierPressed(event)) return
    let node: HTMLElement | null = event.target as HTMLElement | null
    while (node && node !== event.currentTarget) {
      const url = node.dataset?.url
      if (url) {
        event.preventDefault()
        event.stopPropagation()
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }
      node = node.parentElement
    }
  }, [])

  const cell = (
    <Cell
      size="compact"
      data-reminders
      data-popup-open={opensPopover && (rowPopover.open || !!popover.view) ? '' : undefined}
      // No `focused` ring: clicking a row puts the caret in its title, and a blue
      // outline around the whole row on every click reads as a selection the row
      // doesn't have. Cell still draws its own ring for keyboard focus, which is
      // the case that needs one. `isCellFocused` stays — it suppresses the hover
      // card while the title is being edited.
      onClick={handleCellClick}
      onFocus={opensPopover ? rowPopover.onRowFocus : undefined}
      onBlur={opensPopover ? rowPopover.onRowBlur : undefined}
      onFocusCapture={() => setIsCellFocused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setIsCellFocused(false)
      }}
      className={twMerge(
        'flex-col gap-1 px-3',
        showsTags ? 'py-1.5' : 'py-2',
        SIDEPANEL_CELL_SURFACE,
        // No pointer on a row that only opens its popover: the pointer is for a title that leaves for a View.
        opensPopover && `cursor-default ${SIDEPANEL_FILLED_HOVER_SURFACE}`,
        // `hover:` and `group-hover/action:` too: the surface's hover borders are
        // more specific than a plain class and would step the focused border
        // back down to the hover border the moment the pointer is over the row.
        // Not on a row that opens a popover: its title isn't edited in place, and focus coming back
        // from the closed popover would hold the border with nothing being edited.
        isCellFocused && !opensPopover && 'border-strong hover:border-strong group-hover/action:border-strong',
        // One dim for every completed row, lingering or not, so there is no
        // second opacity for a lingering row to flash to when it leaves.
        resolved && 'opacity-60 duration-300 ease-out hover:opacity-80',
      )}
    >
      <div className="flex w-full flex-col gap-1.5">
        <div className="flex w-full items-start gap-2">
          <ReminderCheckbox
            checked={resolved}
            title={item.title}
            mark={mark}
            due={due}
            onCheckedChange={(checked) => {
              setResolved(item.id, checked)
              showSaving()
            }}
          />
          <div
            ref={editRef}
            contentEditable={!opensPopover}
            onInput={handleInput}
            onFocus={(event) => {
              const el = event.currentTarget
              requestAnimationFrame(() => {
                if (!el.textContent) return
                const range = document.createRange()
                const sel = window.getSelection()
                range.selectNodeContents(el)
                range.collapse(false)
                sel?.removeAllRanges()
                sel?.addRange(range)
              })
              markItemSeen(item.id)
              setIsEditingTitle(true)
            }}
            onBlur={() => {
              setIsEditingTitle(false)
              commitEdit()
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onClick={handleEditableClick}
            aria-label={opensPopover ? undefined : 'Edit item'}
            className={twMerge(
              'text-foreground selection:bg-element-active [&::-webkit-scrollbar-thumb]:bg-element-active type-prose-sm min-w-0 flex-1 overflow-y-auto whitespace-pre-wrap break-words bg-transparent font-normal outline-none transition-colors duration-150 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1',
              isRow && 'max-h-[200px]',
              // TODO_TITLE's type on its own too, so the title doesn't change while it's edited.
              showsTags && TODO_TITLE_TYPE,
              showsTags && !isEditingTitle && TODO_TITLE,
              resolved && 'decoration-foreground-placeholder line-through',
            )}
          />
          {showsTags && (
            <span className={givesWayToTodoActions(showsTodoActions)}>
              <ReminderTags item={item} resolved={resolved} due={due} />
            </span>
          )}
          {showsTodoActions && <ReminderTodoActions item={item} onSaving={showSaving} />}
        </div>
        {resolved && item.autoResolved && (
          <p
            className={twMerge('text-foreground-secondary line-clamp-2 text-xs', detailIndent)}
            title={item.autoResolved.evidence}
          >
            Checked off by Highlight · {item.autoResolved.evidence}
          </p>
        )}
        {showMeta && <ReminderMetaRow item={item} resolved={resolved} className={detailIndent} />}
        {hasLinks && !collapsed && (
          <div className={twMerge('flex flex-wrap gap-1', detailIndent)}>
            <ReminderLinkAttachments urls={urls} />
          </div>
        )}
        {hasAttachments && !collapsed && (
          <div className={twMerge(detailIndent, isRow ? 'flex flex-wrap gap-1' : 'flex w-full flex-col gap-2')}>
            {item.attachments?.map((fileId) => (
              <Attachment
                key={fileId}
                fileId={fileId}
                onRemove={() => {
                  removeAttachment(item.id, fileId)
                  showSaving()
                }}
              />
            ))}
          </div>
        )}
      </div>
      {!collapsed && (
        <ReminderSourceRow
          item={item}
          resolved={resolved}
          isSaving={isSaving}
          onSaving={showSaving}
          offersMoves={isRow}
        />
      )}
    </Cell>
  )

  // No card over an open context menu, on a resolved reminder (nothing left
  // to schedule), or while anything in the cell holds focus — a popup over an
  // active title edit would sit on top of the caret.
  // A row that opens a popover shows its detail there instead.
  const hoverSuppressed = opensPopover || ctxOpen || resolved || isCellFocused || !!popover.view

  // Linger still mounts through an open context menu (the take-back can't
  // disappear behind it). Resting buttons don't — the menu owns that click.
  //
  // Unlike the card, in-cell focus doesn't unmount the cluster — unmounting
  // would pull its buttons from the tab order the moment focus enters the cell,
  // so keyboard users could never reach them. Instead the hover reveal is
  // switched off while the cell holds focus (an active title edit owns the
  // pointer); the focus-within reveal stays, so tabbing past the title still
  // reaches Edit / Delete / Mark as done.
  //
  // A row that opens a popover has none: its moves are in the popover.
  const cluster =
    isRow && !opensPopover && (lingering || (!ctxOpen && !resolved)) ? (
      <ReminderRowHoverButtons
        item={item}
        lingering={lingering}
        onEdit={focusTitle}
        onSaving={showSaving}
        className={
          isCellFocused && !lingering
            ? 'group-hover/action:pointer-events-none group-hover/action:opacity-0'
            : undefined
        }
      />
    ) : undefined

  const rowWithCard = (
    <CellPreviewCard
      disabled={hoverSuppressed}
      // `group/cell` up here as well as on the Cell (meeting-row's
      // pattern): the cluster is the cell's sibling, so its hover must
      // still count for the title's `group-hover/cell:` brighten. A row
      // that opens a popover has no cluster, so no `group/action` either.
      triggerClassName={opensPopover ? 'group/cell relative' : 'group/cell group/action relative'}
      density="tight"
      preview={
        <ReminderPreview
          item={item}
          onSnooze={(date) => {
            updateItem(item.id, item.title, date, { trigger: HOVER_CARD_TRIGGER })
            showSaving()
          }}
          onMutated={showSaving}
        />
      }
    >
      {cell}
      {cluster}
    </CellPreviewCard>
  )

  return (
    <>
      <CellContextMenu
        onOpenChange={setCtxOpen}
        items={
          <ReminderContextMenuItems
            item={item}
            resolved={resolved}
            lingering={lingering}
            onEdit={opensPopover ? () => openReminderView() : focusTitle}
            onSaving={showSaving}
            onFeedback={setFeedbackType}
            // The detail cell is already the Reminder View — there is no second
            // window for it to open.
            onPopout={isRow ? openInWindow : undefined}
          />
        }
      >
        {isRow ? (
          // Wrapped only to anchor the popovers, so a row that opens none keeps its DOM.
          opensPopover ? (
            <div ref={rowRef}>{rowWithCard}</div>
          ) : (
            rowWithCard
          )
        ) : (
          cell
        )}
      </CellContextMenu>
      {opensPopover && (
        <>
          <ReminderPopover
            item={item}
            open={rowPopover.open}
            previewing={rowPopover.previewing}
            swapping={rowPopover.swapping}
            onOpenChange={rowPopover.onOpenChange}
            anchor={rowRef}
            onSaving={showSaving}
            onClose={rowPopover.close}
          />
          <ViewPopover
            view={popover.view}
            anchor={rowRef}
            onClose={popover.close}
            closeRequest={popover.closeRequest}
          />
        </>
      )}

      {feedback && (
        <MessageFeedbackPopover
          subject={feedback.subject}
          initialRating={feedback.initialRating}
          source="reminder"
          type={feedbackType}
          onClose={() => setFeedbackType(null)}
          anchor={editRef}
        />
      )}
    </>
  )
})

export { ReminderCell }
